import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import api from '../api/client';
import { useAiCache, AI_TTL } from '../contexts/AiCacheContext';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Activity, Heart, AlertTriangle, TrendingUp, TrendingDown,
  Pill, FileText, Shield, Calendar, Clock, CheckCircle, Phone,
  MessageSquare, ChevronRight, RefreshCw, Stethoscope, Users,
  UserPlus, Plus, X, Pencil, Trash2, Check, Thermometer, FlaskConical, Brain, CalendarClock, AlertCircle, Zap,
  Lock, Loader2
} from 'lucide-react';
import VitalsCard from '../components/VitalsCard';
import LabResultsCard from '../components/LabResultsCard';

// Condition-aware care plan templates. Each entry auto-fills the transition form.
const CARE_PLAN_TEMPLATES = [
  {
    id: 'chf', label: 'CHF / Heart Failure',
    keywords: ['heart failure', 'chf', 'congestive'],
    reason: 'Congestive Heart Failure post-discharge monitoring',
    followUpDays: 7, followUpType: 'virtual_visit',
  },
  {
    id: 'copd', label: 'COPD / Respiratory',
    keywords: ['copd', 'chronic obstructive', 'emphysema', 'bronchitis'],
    reason: 'COPD exacerbation post-discharge monitoring',
    followUpDays: 5, followUpType: 'virtual_visit',
  },
  {
    id: 'ortho', label: 'Hip / Knee Replacement',
    keywords: ['hip', 'knee', 'arthroplasty', 'orthopedic', 'joint replacement'],
    reason: 'Post-surgical orthopedic follow-up',
    followUpDays: 14, followUpType: 'home_visit',
  },
  {
    id: 'stroke', label: 'Stroke / TIA',
    keywords: ['stroke', 'tia', 'transient ischemic', 'cerebrovascular'],
    reason: 'Post-stroke neurological monitoring',
    followUpDays: 7, followUpType: 'virtual_visit',
  },
  {
    id: 'pneumonia', label: 'Pneumonia',
    keywords: ['pneumonia', 'respiratory infection', 'lung infection'],
    reason: 'Pneumonia post-discharge monitoring',
    followUpDays: 7, followUpType: 'virtual_visit',
  },
  {
    id: 'general', label: 'General Post-Discharge',
    keywords: [],
    reason: 'Post-discharge monitoring',
    followUpDays: 14, followUpType: 'virtual_visit',
  },
];

const PatientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getOrFetch } = useAiCache();
  const isDoctor = user?.role === 'DOCTOR';
  const [profile, setProfile] = useState(null);
  const [riskHistory, setRiskHistory] = useState([]);
  const [careEvents, setCareEvents] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggeringAssessment, setTriggeringAssessment] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [isAlreadyDoctor, setIsAlreadyDoctor] = useState(false);
  const [restrictedView, setRestrictedView] = useState(null);
  const [consentMode, setConsentMode] = useState('full');
  const [consentScope, setConsentScope] = useState(null); // null = no restriction
  const [symptomPicker, setSymptomPicker] = useState(false);
  const [showAddCondition, setShowAddCondition] = useState(false);
  const [condForm, setCondForm] = useState({ name: '', severity: 'mild', diagnosedDate: '' });
  const [savingCond, setSavingCond] = useState(false);
  const [showAddMed, setShowAddMed] = useState(false);
  const [medForm, setMedForm] = useState({ name: '', dosage: '', frequency: 'daily', prescribedBy: '' });
  const [savingMed, setSavingMed] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // { type, id, form }
  const [savingEdit, setSavingEdit] = useState(false);
  const [showAddTransition, setShowAddTransition] = useState(false);
  const [transForm, setTransForm] = useState({ type: 'discharge', date: '', facility: '', reason: '', followUpRequired: false, followUpDate: '', followUpTime: '09:00', followUpType: 'virtual_visit' });
  const [savingTrans, setSavingTrans] = useState(false);
  const [doctorAppointments, setDoctorAppointments] = useState([]);
  const [loadingAppts, setLoadingAppts] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [caregivers, setCaregivers] = useState([]);
  const [doctorNotes, setDoctorNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newNoteTag, setNewNoteTag] = useState('clinical');
  const [savingNote, setSavingNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null); // { id, note, tag }
  const [deletingNote, setDeletingNote] = useState(null);
  const [noteFilter, setNoteFilter] = useState('all');
  const [notePage, setNotePage] = useState(1);
  const NOTES_PER_PAGE = 5;

  const loadProfile = async () => {
    try {
      setLoading(true);
      setRestrictedView(null);

      let profileRes;
      try {
        profileRes = await api.get(`/patient-profile/${id}`);
      } catch (profileErr) {
        // If 403 (no consent / not on care team), fall back to summary endpoint
        if (profileErr.response?.status === 403) {
          try {
            const summaryRes = await api.get(`/patient-profile/${id}/summary`);
            if (summaryRes.data?.success) {
              setRestrictedView(summaryRes.data.data);
            }
          } catch {
            // Summary also failed — show generic error
          }
          return; // Don't fetch other data — no access
        }
        throw profileErr; // Re-throw non-403 errors
      }

      if (profileRes.data?.success) {
        const mode = profileRes.data.consentMode || 'full';
        const scope = profileRes.data.consentScope || null;
        setConsentMode(mode);
        setConsentScope(scope);
        const responseData = profileRes.data.data;
        const profileData = responseData?.profile || responseData;
        setProfile(profileData);
        const doctorId = user?._id || user?.id;
        if (isDoctor && profileData?.careTeam) {
          const alreadyDoctor = profileData.careTeam.some(member => {
            const memberId = member.providerId?._id || member.providerId;
            return memberId === doctorId;
          });
          setIsAlreadyDoctor(alreadyDoctor);
        }

        // Restricted mode — skip all detailed data fetches
        if (mode === 'restricted') return;
      }

      // Load caregivers and doctor notes for doctor view (non-blocking, best-effort)
      if (isDoctor) {
        api.get(`/caregiver/patient/${id}`).then(r => setCaregivers(r.data.data || [])).catch(() => {});
        api.get(`/doctor-notes/${id}`).then(r => setDoctorNotes(r.data.data || [])).catch(() => {});
      }

      const fetchScope = profileRes.data?.consentScope || null;
      const [riskRes, eventsRes, symptomsRes] = await Promise.all([
        api.get(`/risk-assessments/patient/${id}?days=30`).catch(() => null),
        (!fetchScope || fetchScope.careEvents !== false)
          ? api.get(`/care-events/patient/${id}?days=30&limit=20`).catch(() => null)
          : Promise.resolve(null),
        (!fetchScope || fetchScope.symptoms !== false)
          ? api.get(`/symptoms?status=ALL&page=1&limit=20&patientId=${id}`).catch(() => null)
          : Promise.resolve(null),
      ]);

      if (riskRes?.data?.data) setRiskHistory(riskRes.data.data.history || []);
      if (eventsRes?.data?.data) setCareEvents(eventsRes.data.data || []);
      if (symptomsRes?.data?.data) setSymptoms(symptomsRes.data.data || []);

      // Fetch AI summary for doctor view (non-blocking)
      if (isDoctor) {
        setAiSummaryLoading(true);
        getOrFetch(`patient-summary:${id}`, () => api.get(`/ai/patient-summary/${id}`).then(r => r.data.summary || null), AI_TTL.PATIENT_SUMMARY)
          .then(summary => setAiSummary(summary))
          .catch(() => {})
          .finally(() => setAiSummaryLoading(false));
      }
    } catch (err) {
      console.error('Error loading patient:', err);
      toast.error('Failed to load patient details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfile(); }, [id]);

  const handleTriggerAssessment = async () => {
    setTriggeringAssessment(true);
    try {
      await api.post(`/risk-assessments/trigger/${id}`);
      toast.success('Risk assessment triggered');
      const riskRes = await api.get(`/risk-assessments/patient/${id}?days=30`);
      if (riskRes.data?.data) setRiskHistory(riskRes.data.data.history || []);
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to trigger assessment';
      toast.error(detail);
    } finally {
      setTriggeringAssessment(false);
    }
  };

  // Combine followUpDate + followUpTime into an ISO string
  const buildFollowUpISO = (date, time) => {
    if (!date) return null;
    const [h, m] = (time || '09:00').split(':').map(Number);
    const d = new Date(date);
    d.setHours(h, m, 0, 0);
    return d.toISOString();
  };

  const fetchDoctorAppointments = async () => {
    setLoadingAppts(true);
    try {
      const res = await api.get('/care-events/my-appointments?days=60');
      setDoctorAppointments(res.data?.data || []);
    } catch { /* non-blocking */ }
    finally { setLoadingAppts(false); }
  };

  // Returns conflicting appointment if selected slot ±60 min overlaps an existing one
  const getConflict = (date, time) => {
    if (!date || !time || !doctorAppointments.length) return null;
    const selected = new Date(buildFollowUpISO(date, time));
    return doctorAppointments.find(appt => {
      const apptDate = new Date(appt.appointmentData?.scheduledDate || appt.data?.scheduledDate);
      return Math.abs(apptDate - selected) < 60 * 60 * 1000;
    }) || null;
  };

  // Templates ranked by match against active conditions; unmatched ones shown at end
  const suggestedTemplates = (() => {
    const activeNames = (profile?.conditions || [])
      .filter(c => c.status === 'active')
      .map(c => c.name?.toLowerCase() || '');
    const matched = [], rest = [];
    CARE_PLAN_TEMPLATES.forEach(t => {
      const hits = t.keywords.some(kw => activeNames.some(n => n.includes(kw)));
      (hits ? matched : rest).push({ ...t, matched: hits });
    });
    return [...matched, ...rest];
  })();

  const applyTemplate = (tpl) => {
    const dischargeDate = transForm.date;
    let followUpDate = '';
    if (dischargeDate) {
      const d = new Date(dischargeDate);
      d.setDate(d.getDate() + tpl.followUpDays);
      followUpDate = d.toISOString().slice(0, 10);
    }
    setTransForm(p => ({
      ...p,
      type: 'discharge',
      reason: tpl.reason,
      followUpRequired: true,
      followUpDate,
      followUpType: tpl.followUpType,
    }));
    if (!doctorAppointments.length) fetchDoctorAppointments();
  };

  const handleAddTransition = async () => {
    if (!transForm.date) { toast.error('Date is required'); return; }
    setSavingTrans(true);
    const payload = {
      ...transForm,
      followUpDate: transForm.followUpRequired ? buildFollowUpISO(transForm.followUpDate, transForm.followUpTime) : null,
    };
    try {
      await api.post(`/patient-profile/${id}/transitions`, payload);
      toast.success('Transition recorded');
      setShowAddTransition(false);
      setTransForm({ type: 'discharge', date: '', facility: '', reason: '', followUpRequired: false, followUpDate: '', followUpTime: '09:00', followUpType: 'virtual_visit' });
      setDoctorAppointments([]);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to record transition'); }
    finally { setSavingTrans(false); }
  };

  const apiPath = (type) => type === 'transition' ? 'transitions' : `${type}s`;

  const handleDeleteItem = async (type, itemId) => {
    if (!confirm('Remove this entry from the patient record?')) return;
    try {
      await api.delete(`/patient-profile/${id}/${apiPath(type)}/${itemId}`);
      toast.success('Removed');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to remove'); }
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    setSavingEdit(true);
    try {
      const payload = editingItem.type === 'transition' && editingItem.form.followUpRequired
        ? { ...editingItem.form, followUpDate: buildFollowUpISO(editingItem.form.followUpDate?.slice(0,10), editingItem.form.followUpTime) }
        : editingItem.form;
      await api.put(`/patient-profile/${id}/${apiPath(editingItem.type)}/${editingItem.id}`, payload);
      toast.success('Updated');
      setEditingItem(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to update'); }
    finally { setSavingEdit(false); }
  };

  const handleAddCondition = async () => {
    if (!condForm.name || !condForm.diagnosedDate) { toast.error('Name and date required'); return; }
    setSavingCond(true);
    try {
      await api.post(`/patient-profile/${id}/conditions`, condForm);
      toast.success('Condition added');
      setShowAddCondition(false);
      setCondForm({ name: '', severity: 'mild', diagnosedDate: '' });
      const profileRes = await api.get(`/patient-profile/${id}`);
      if (profileRes.data?.success) {
        const d = profileRes.data.data;
        setProfile(d?.profile || d);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add condition');
    } finally {
      setSavingCond(false);
    }
  };

  const handleAddMedication = async () => {
    if (!medForm.name) { toast.error('Medication name required'); return; }
    setSavingMed(true);
    try {
      await api.post(`/patient-profile/${id}/medications`, medForm);
      toast.success('Medication added');
      setShowAddMed(false);
      setMedForm({ name: '', dosage: '', frequency: 'daily', prescribedBy: '' });
      const profileRes = await api.get(`/patient-profile/${id}`);
      if (profileRes.data?.success) {
        const d = profileRes.data.data;
        setProfile(d?.profile || d);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add medication');
    } finally {
      setSavingMed(false);
    }
  };

  const handleRequestToJoin = async () => {
    setSendingRequest(true);
    try {
      await api.post('/care-requests/send', {
        patientId: id,
        requestedRole: 'specialist',
        message: `Dr. ${user?.name} would like to join your care team and help monitor your health.`
      });
      toast.success('Care team request sent! Waiting for patient approval.');
      // Refresh restricted view to show pending request status
      if (restrictedView) {
        try {
          const summaryRes = await api.get(`/patient-profile/${id}/summary`);
          if (summaryRes.data?.success) setRestrictedView(summaryRes.data.data);
        } catch {}
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send request');
    } finally {
      setSendingRequest(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="h-10 w-32 rounded-xl animate-pulse bg-slate-100" />
        <div className="h-64 rounded-2xl animate-pulse bg-slate-100" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-48 rounded-2xl animate-pulse bg-slate-100" />
          <div className="h-48 rounded-2xl animate-pulse bg-slate-100" />
        </div>
      </div>
    );
  }

  // Restricted view: doctor has no consent and is not on care team
  if (restrictedView) {
    const rv = restrictedView;
    const initials = (rv.name || 'P').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return (
      <div className="space-y-8 py-6">
        

        <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-8 text-center space-y-6">
          {/* Patient avatar */}
          <div className="w-20 h-20 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-2xl font-extrabold mx-auto">
            {initials}
          </div>

          <div>
            <h2 className="font-headline text-2xl font-extrabold text-slate-900">{rv.name}</h2>
            <p className="text-sm text-slate-500 mt-1">
              Member since {new Date(rv.memberSince).toLocaleDateString()} · {rv.careTeamSize} care team member{rv.careTeamSize !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Consent wall */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 max-w-md mx-auto">
            <Shield className="w-8 h-8 text-amber-600 mx-auto mb-3" />
            <h3 className="font-bold text-amber-900 mb-1">Access Restricted</h3>
            <p className="text-sm text-amber-700">
              You are not currently on this patient's care team. Request to join to view their full health profile.
            </p>
          </div>

          {/* CTA */}
          {rv.pendingRequest ? (
            <div className="bg-slate-50 rounded-2xl p-5 max-w-md mx-auto">
              <Clock className="w-6 h-6 text-slate-500 mx-auto mb-2" />
              <p className="font-bold text-sm text-slate-700">Request pending</p>
              <p className="text-xs text-slate-500 mt-1">
                Sent {new Date(rv.pendingRequest.requestedAt).toLocaleDateString()} — waiting for patient approval
              </p>
            </div>
          ) : (
            <button
              disabled={sendingRequest}
              onClick={handleRequestToJoin}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-white rounded-2xl font-bold shadow-md hover:shadow-lg active:scale-95 transition-all disabled:opacity-50"
            >
              <UserPlus className="w-5 h-5" />
              {sendingRequest ? 'Sending Request...' : 'Request to Join Care Team'}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-20">
        <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <h3 className="font-bold text-lg text-slate-800 mb-1">Patient not found</h3>
        <button onClick={() => navigate(-1)} className="mt-4 text-primary font-bold text-sm">Go back</button>
      </div>
    );
  }

  const patient = profile.userId || {};
  const name = patient.name || 'Patient';
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const riskScore = profile.currentRisk?.overall || 0;
  const latestAssessment = riskHistory[0];
  const trend = latestAssessment?.trend || 'stable';
  const activeConditions = (profile.conditions || []).filter(c => c.status === 'active');
  const activeMeds = (profile.medications || []).filter(m => m.status === 'active');
  const openSymptoms = symptoms.filter(s => s.status === 'OPEN');

  const symptomPickerModal = symptomPicker && (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={() => setSymptomPicker(false)}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-lg text-slate-900 mb-1">Choose a symptom thread</h3>
        <p className="text-xs text-slate-500 mb-4">{openSymptoms.length} active reports</p>
        <div className="space-y-2">
          {openSymptoms.map(s => (
            <button key={s._id} onClick={() => { setSymptomPicker(false); navigate(`/chat/${s._id}`); }}
              className="w-full p-4 bg-slate-50 hover:bg-primary/5 rounded-xl text-left transition-colors flex items-center gap-3">
              <MessageSquare className="w-4 h-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-slate-900 truncate">{s.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 capitalize">{s.severity} · {new Date(s.createdAt).toLocaleDateString()}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const isRestricted = consentMode === 'restricted';

  const riskColor = riskScore >= 70 ? 'text-red-600' : riskScore >= 45 ? 'text-amber-600' : 'text-emerald-600';

  // Scope guards — null scope means no restriction (care team implicit full access)
  const canViewMedications  = !isRestricted && (!consentScope || consentScope.medications  !== false);
  const canViewConditions   = !isRestricted && (!consentScope || consentScope.conditions   !== false);
  const canViewSymptoms     = !isRestricted && (!consentScope || consentScope.symptoms     !== false);
  const canViewCareEvents   = !isRestricted && (!consentScope || consentScope.careEvents   !== false);
  const canViewVitals       = !isRestricted && (!consentScope || consentScope.vitals       !== false);
  const canViewLabResults   = !isRestricted && (!consentScope || consentScope.labResults   !== false);
  const canViewMentalHealth = !isRestricted && (!consentScope || consentScope.mentalHealth !== false);
  const canViewAllergies    = !isRestricted && (!consentScope || consentScope.allergies    !== false);
  const canViewSocialHistory = !isRestricted && (!consentScope || consentScope.socialHistory !== false);
  const riskBg = riskScore >= 70 ? 'bg-red-50' : riskScore >= 45 ? 'bg-amber-50' : 'bg-emerald-50';
  const riskLabel = riskScore >= 70 ? 'Critical' : riskScore >= 45 ? 'Moderate' : 'Low Risk';
  const riskBarColor = riskScore >= 70 ? 'bg-red-500' : riskScore >= 45 ? 'bg-amber-500' : 'bg-emerald-500';

  const trendCfg = {
    worsening: { icon: TrendingUp, color: 'text-red-500', label: 'Worsening' },
    improving: { icon: TrendingDown, color: 'text-emerald-500', label: 'Improving' },
    stable: { icon: Activity, color: 'text-slate-500', label: 'Stable' },
    critical: { icon: AlertTriangle, color: 'text-red-500', label: 'Critical' },
  };
  const trendInfo = trendCfg[trend] || trendCfg.stable;
  const TrendIcon = trendInfo.icon;

  const sevStyle = (sev) => {
    const m = {
      urgent: 'bg-red-50 text-red-600 border-red-100',
      high: 'bg-orange-50 text-orange-600 border-orange-100',
      medium: 'bg-amber-50 text-amber-700 border-amber-100',
      low: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    };
    return m[sev] || m.medium;
  };

  const eventIcon = (type) => {
    const map = {
      symptom_reported: Activity,
      medication_issue: Pill,
      care_transition: FileText,
      device_reading: Heart,
      mood_checkin: Heart,
      action_executed: CheckCircle,
      action_dismissed: Shield,
      patient_distress: AlertTriangle,
    };
    return map[type] || Clock;
  };

  const timeSince = (date) => {
    const ms = Date.now() - new Date(date).getTime();
    const hours = Math.floor(ms / 3600000);
    if (hours < 1) return `${Math.floor(ms / 60000)}m ago`;
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString();
  };

  // Score breakdown from latest assessment
  const scores = latestAssessment?.scores || profile.currentRisk || {};
  const scoreBreakdown = [
    { label: 'Readmission', value: scores.readmissionRisk?.value || scores.readmission || 0, color: '#ba1a1a' },
    { label: 'Deterioration', value: scores.deteriorationRisk?.value || scores.deterioration || 0, color: '#F97316' },
    { label: 'Medication', value: scores.medicationAdherenceRisk?.value || scores.medicationAdherence || 0, color: '#5f4300' },
    { label: 'Care Breakdown', value: scores.careBreakdownRisk?.value || scores.careBreakdown || 0, color: '#00478d' },
  ];

  return (
    <div className="space-y-8">

      {/* ─── Restricted Access Banner ─── */}
      {isRestricted && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
          <Shield className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-900">Limited View — Data Sharing Restricted</p>
            <p className="text-xs text-amber-700 mt-0.5">
              This patient has turned off data sharing. You can see emergency-critical conditions only.
              Medications, symptom history, and risk scores are hidden per patient privacy settings.
            </p>
          </div>
        </div>
      )}

      {/* ─── Patient Hero Card ─── */}
      <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -mr-32 -mt-32 blur-[80px] pointer-events-none" />
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
          <div className="flex items-center gap-5">
            <div className={`w-20 h-20 rounded-3xl flex items-center justify-center font-extrabold text-2xl ${riskBg} ${riskColor}`}>
              {initials}
            </div>
            <div>
              <h1 className="font-headline text-3xl font-extrabold text-slate-900 tracking-tight">{name}</h1>
              <p className="text-sm text-slate-500 mt-0.5">{patient.email} • ID: #{(id || '').slice(-6).toUpperCase()}</p>
              <div className="flex items-center gap-3 mt-2">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${riskBg} ${riskColor} border ${riskScore >= 70 ? 'border-red-100' : riskScore >= 45 ? 'border-amber-100' : 'border-emerald-100'}`}>
                  {riskLabel}
                </span>
                <div className="flex items-center gap-1">
                  <TrendIcon className={`w-3.5 h-3.5 ${trendInfo.color}`} />
                  <span className={`text-xs font-semibold ${trendInfo.color}`}>{trendInfo.label}</span>
                </div>
                {activeConditions.length > 0 && (
                  <span className="text-xs text-slate-500">{activeConditions.length} condition{activeConditions.length > 1 ? 's' : ''}</span>
                )}
              </div>
            </div>
          </div>

          {/* Risk Score Dial */}
          <div className="flex items-center gap-8">
            <div className="text-center">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Risk Score</p>
              <div className={`text-5xl font-black tracking-tighter ${riskColor}`}>{riskScore}</div>
              <div className="w-24 bg-slate-100 h-1.5 rounded-full overflow-hidden mt-2">
                <div className={`h-full rounded-full transition-all duration-700 ${riskBarColor}`} style={{ width: `${Math.min(riskScore, 100)}%` }} />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {isDoctor && !isAlreadyDoctor && (
                <button
                  onClick={handleRequestToJoin}
                  disabled={sendingRequest}
                  className="px-5 py-2.5 bg-emerald-600 text-white text-xs font-bold rounded-xl hover:bg-emerald-700 transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  {sendingRequest ? 'Sending...' : 'Request to Join'}
                </button>
              )}
              {isDoctor && isAlreadyDoctor && (
                <div className="px-5 py-2.5 bg-slate-100 text-slate-600 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  In Care Team
                </div>
              )}
              <button
                onClick={handleTriggerAssessment}
                disabled={triggeringAssessment}
                className="px-5 py-2.5 bg-slate-900 text-white text-xs font-bold rounded-xl hover:bg-primary transition-all disabled:opacity-50"
              >
                {triggeringAssessment ? 'Assessing...' : 'Run Assessment'}
              </button>
              <button
                onClick={() => {
                  if (openSymptoms.length === 1) navigate(`/chat/${openSymptoms[0]._id}`);
                  else if (openSymptoms.length > 1) setSymptomPicker(true);
                }}
                disabled={openSymptoms.length === 0}
                className="px-5 py-2.5 bg-primary/5 text-primary border border-primary/10 text-xs font-bold rounded-xl hover:bg-primary hover:text-white transition-all disabled:opacity-50"
              >
                <MessageSquare className="w-3.5 h-3.5 inline mr-1.5" />Message
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── AI Clinical Summary Card ─── */}
      {isDoctor && (aiSummaryLoading || aiSummary) && (
        <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-100 rounded-2xl px-6 py-5 flex gap-4 items-start shadow-sm">
          <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center shrink-0 mt-0.5">
            <Zap className="w-4 h-4 text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black text-violet-500 uppercase tracking-widest mb-1.5">AI Clinical Summary</p>
            {aiSummaryLoading ? (
              <div className="flex items-center gap-2.5">
                <div className="flex gap-1">
                  {[0,1,2].map(i => (
                    <div key={i} className="w-1.5 h-1.5 rounded-full bg-violet-300 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
                <span className="text-xs text-violet-400">Generating clinical summary…</span>
              </div>
            ) : (
              <p className="text-sm text-slate-700 leading-relaxed">{aiSummary}</p>
            )}
          </div>
        </div>
      )}

      {/* ─── Risk Score Breakdown ─── */}
      {!isRestricted && <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {scoreBreakdown.map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">{s.label}</p>
            <div className="flex items-end gap-2">
              <span className="text-2xl font-extrabold text-slate-900 tracking-tighter">{Math.round(s.value)}</span>
              <span className="text-xs text-slate-500 mb-0.5">/100</span>
            </div>
            <div className="w-full bg-slate-100 h-1 rounded-full overflow-hidden mt-2">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(s.value, 100)}%`, backgroundColor: s.color }} />
            </div>
          </div>
        ))}
      </div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ─── Active Conditions ─── */}
        <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-7">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-headline text-lg font-bold text-slate-900">Active Conditions</h3>
            <div className="flex items-center gap-3">
              {canViewConditions && <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{activeConditions.length} active</span>}
              {isDoctor && isAlreadyDoctor && canViewConditions && (
                <button onClick={() => setShowAddCondition(v => !v)} className="flex items-center gap-1 text-xs font-bold text-primary hover:bg-primary/10 px-2.5 py-1.5 rounded-lg transition-all">
                  {showAddCondition ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                  {showAddCondition ? 'Cancel' : 'Add'}
                </button>
              )}
            </div>
          </div>
          {!canViewConditions && <div className="flex items-center gap-2 py-6 justify-center"><Shield className="w-4 h-4 text-slate-400"/><p className="text-sm text-slate-400">Conditions access not granted</p></div>}
          {canViewConditions && showAddCondition && (
            <div className="mb-4 p-4 bg-slate-50 rounded-xl space-y-3 border border-slate-200">
              <input placeholder="Condition name *" value={condForm.name} onChange={e => setCondForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              <div className="grid grid-cols-2 gap-2">
                <select value={condForm.severity} onChange={e => setCondForm(p => ({ ...p, severity: e.target.value }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg">
                  <option value="mild">Mild</option>
                  <option value="moderate">Moderate</option>
                  <option value="severe">Severe</option>
                </select>
                <input type="date" value={condForm.diagnosedDate} onChange={e => setCondForm(p => ({ ...p, diagnosedDate: e.target.value }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" />
              </div>
              <button onClick={handleAddCondition} disabled={savingCond} className="w-full py-2 bg-primary text-white rounded-lg text-sm font-bold disabled:opacity-50">
                {savingCond ? 'Saving...' : 'Save Condition'}
              </button>
            </div>
          )}
          {canViewConditions && activeConditions.length > 0 ? (
            <div className="space-y-3">
              {activeConditions.map((c) => (
                <div key={c._id}>
                  {editingItem?.id === c._id ? (
                    <div className="p-3 bg-slate-50 rounded-xl border border-primary/20 space-y-2">
                      <input value={editingItem.form.name} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, name: e.target.value } }))} className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary" placeholder="Condition name" />
                      <div className="grid grid-cols-2 gap-2">
                        <select value={editingItem.form.severity} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, severity: e.target.value } }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg">
                          <option value="mild">Mild</option><option value="moderate">Moderate</option><option value="severe">Severe</option>
                        </select>
                        <input type="date" value={editingItem.form.diagnosedDate?.slice(0,10)} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, diagnosedDate: e.target.value } }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={handleSaveEdit} disabled={savingEdit} className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-primary text-white rounded-lg text-xs font-bold disabled:opacity-50"><Check className="w-3.5 h-3.5" />{savingEdit ? 'Saving…' : 'Save'}</button>
                        <button onClick={() => setEditingItem(null)} className="flex-1 py-1.5 bg-slate-200 text-slate-600 rounded-lg text-xs font-bold">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="group flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-outline-variant/10">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${c.severity === 'severe' ? 'bg-red-500' : c.severity === 'moderate' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{c.name}</p>
                          <p className="text-xs text-slate-500">Diagnosed: {new Date(c.diagnosedDate).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${c.severity === 'severe' ? 'bg-red-50 text-red-600' : c.severity === 'moderate' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>{c.severity}</span>
                        {isDoctor && isAlreadyDoctor && canViewConditions && (<>
                          <button onClick={() => setEditingItem({ type: 'condition', id: c._id, form: { name: c.name, severity: c.severity, diagnosedDate: c.diagnosedDate } })} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-primary/10 rounded-lg transition-all"><Pencil className="w-3.5 h-3.5 text-primary" /></button>
                          <button onClick={() => handleDeleteItem('condition', c._id)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded-lg transition-all"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                        </>)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : canViewConditions ? (
            <p className="text-sm text-slate-500 text-center py-6">No active conditions</p>
          ) : null}
        </div>

        {/* ─── Medications ─── */}
        {<div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-7">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-headline text-lg font-bold text-slate-900">Medications</h3>
            <div className="flex items-center gap-3">
              {canViewMedications && <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{activeMeds.length} active</span>}
              {isDoctor && isAlreadyDoctor && canViewMedications && (
                <button onClick={() => setShowAddMed(v => !v)} className="flex items-center gap-1 text-xs font-bold text-primary hover:bg-primary/10 px-2.5 py-1.5 rounded-lg transition-all">
                  {showAddMed ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                  {showAddMed ? 'Cancel' : 'Add'}
                </button>
              )}
            </div>
          </div>
          {!canViewMedications && <div className="flex items-center gap-2 py-6 justify-center"><Shield className="w-4 h-4 text-slate-400"/><p className="text-sm text-slate-400">Medications access not granted</p></div>}
          {canViewMedications && showAddMed && (
            <div className="mb-4 p-4 bg-slate-50 rounded-xl space-y-3 border border-slate-200">
              <input placeholder="Medication name *" value={medForm.name} onChange={e => setMedForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Dosage (e.g. 10mg)" value={medForm.dosage} onChange={e => setMedForm(p => ({ ...p, dosage: e.target.value }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" />
                <select value={medForm.frequency} onChange={e => setMedForm(p => ({ ...p, frequency: e.target.value }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg">
                  <option value="daily">Daily</option>
                  <option value="twice daily">Twice daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="as needed">As needed</option>
                </select>
              </div>
              <button onClick={handleAddMedication} disabled={savingMed} className="w-full py-2 bg-primary text-white rounded-lg text-sm font-bold disabled:opacity-50">
                {savingMed ? 'Saving...' : 'Save Medication'}
              </button>
            </div>
          )}
          {canViewMedications && activeMeds.length > 0 ? (
            <div className="space-y-3">
              {activeMeds.map((m) => (
                <div key={m._id}>
                  {editingItem?.id === m._id ? (
                    <div className="p-3 bg-slate-50 rounded-xl border border-primary/20 space-y-2">
                      <input value={editingItem.form.name} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, name: e.target.value } }))} className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" placeholder="Medication name" />
                      <div className="grid grid-cols-2 gap-2">
                        <input value={editingItem.form.dosage} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, dosage: e.target.value } }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" placeholder="Dosage" />
                        <select value={editingItem.form.frequency} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, frequency: e.target.value } }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg">
                          <option value="daily">Daily</option><option value="twice daily">Twice daily</option><option value="three times daily">Three times daily</option><option value="weekly">Weekly</option><option value="as needed">As needed</option>
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={handleSaveEdit} disabled={savingEdit} className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-primary text-white rounded-lg text-xs font-bold disabled:opacity-50"><Check className="w-3.5 h-3.5" />{savingEdit ? 'Saving…' : 'Save'}</button>
                        <button onClick={() => setEditingItem(null)} className="flex-1 py-1.5 bg-slate-200 text-slate-600 rounded-lg text-xs font-bold">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="group flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-outline-variant/10">
                      <div className="flex items-center gap-3">
                        <Pill className="w-4 h-4 text-primary" />
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{m.name}</p>
                          <p className="text-xs text-slate-500">{m.dosage} • {m.frequency}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {m.adherence && (
                          <div className="text-right">
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Adherence</p>
                            <p className={`text-sm font-extrabold ${(m.adherence.adherenceRate ?? 100) >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>{m.adherence.adherenceRate ?? '—'}%</p>
                          </div>
                        )}
                        {isDoctor && isAlreadyDoctor && canViewMedications && (<>
                          <button onClick={() => setEditingItem({ type: 'medication', id: m._id, form: { name: m.name, dosage: m.dosage || '', frequency: m.frequency } })} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-primary/10 rounded-lg transition-all"><Pencil className="w-3.5 h-3.5 text-primary" /></button>
                          <button onClick={() => handleDeleteItem('medication', m._id)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded-lg transition-all"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                        </>)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : canViewMedications ? (
            <p className="text-sm text-slate-500 text-center py-6">No active medications</p>
          ) : null}
        </div>}
      </div>

      {/* ─── Symptoms ─── */}
      {<div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 overflow-hidden">
        <div className="p-7 pb-0 flex items-center justify-between">
          <h3 className="font-headline text-lg font-bold text-slate-900">Symptom Reports</h3>
          {canViewSymptoms && <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{openSymptoms.length} active / {symptoms.length} total</span>}
        </div>
        {!canViewSymptoms ? (
          <div className="flex items-center gap-2 py-8 justify-center"><Shield className="w-4 h-4 text-slate-400"/><p className="text-sm text-slate-400">Symptom reports access not granted</p></div>
        ) : symptoms.length > 0 ? (
          <div className="divide-y divide-outline-variant/5">
            {symptoms.slice(0, 8).map((s) => (
              <div
                key={s._id}
                onClick={() => navigate(`/symptom/${s._id}`)}
                className="flex items-center justify-between px-7 py-4 hover:bg-slate-50/50 transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${s.status === 'OPEN' ? 'bg-primary' : 'bg-slate-300'}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 group-hover:text-primary transition-colors break-words">{s.title}</p>
                    <p className="text-xs text-slate-500">{timeSince(s.createdAt)} • {s.category}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${sevStyle(s.severity)}`}>
                    {s.severity}
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-primary transition-colors" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-8">No symptom reports</p>
        )}
      </div>}

      {/* ─── Care Events Timeline ─── */}
      {<div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-7">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-headline text-lg font-bold text-slate-900">Care Events (30 days)</h3>
          {canViewCareEvents && <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{careEvents.length} events</span>}
        </div>
        {!canViewCareEvents ? (
          <div className="flex items-center gap-2 py-6 justify-center"><Shield className="w-4 h-4 text-slate-400"/><p className="text-sm text-slate-400">Care events access not granted</p></div>
        ) : careEvents.length > 0 ? (
          <div className="space-y-0">
            {careEvents.slice(0, 12).map((e, i) => {
              const Icon = eventIcon(e.eventType);
              const isLast = i === Math.min(careEvents.length, 12) - 1;
              return (
                <div key={e._id || i} className="flex gap-4 relative">
                  {/* Timeline line */}
                  {!isLast && <div className="absolute left-[15px] top-9 w-px h-full bg-slate-100" />}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 z-10 ${
                    e.severity === 'high' || e.severity === 'critical' ? 'bg-red-50 text-red-500' :
                    e.severity === 'medium' ? 'bg-amber-50 text-amber-500' : 'bg-slate-50 text-slate-500'
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="pb-5 flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-slate-900 capitalize">{e.eventType?.replace(/_/g, ' ')}</p>
                      <span className="text-xs text-slate-500">{timeSince(e.timestamp || e.createdAt)}</span>
                    </div>
                    {e.data?.title && <p className="text-xs text-slate-500 mt-0.5">{e.data.title}</p>}
                    {e.data?.description && <p className="text-xs text-slate-500 mt-0.5 break-words">{e.data.description}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-6">No recent care events</p>
        )}
      </div>}

      {/* ─── Allergies ─── */}
      {(() => {
        const allergies = profile.allergies || [];
        const critical = allergies.filter(a => a.severity === 'life_threatening');
        return (
          <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-7">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-headline text-lg font-bold text-slate-900">Allergies</h3>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${
                critical.length > 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'
              }`}>
                {allergies.length} recorded
              </span>
            </div>
            {critical.length > 0 && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-extrabold text-red-700">Life-threatening allerg{critical.length > 1 ? 'ies' : 'y'}</p>
                  <p className="text-xs text-red-600 mt-0.5">{critical.map(a => a.allergen).join(', ')}</p>
                </div>
              </div>
            )}
            {allergies.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No allergies recorded by patient</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {allergies.map((a, i) => (
                  <div key={i} className={`flex items-center justify-between p-3 rounded-xl border ${
                    a.severity === 'life_threatening' ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-outline-variant/10'
                  }`}>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{a.allergen}</p>
                      {a.reaction && <p className="text-xs text-slate-500 mt-0.5">{a.reaction}</p>}
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                      a.severity === 'life_threatening' ? 'bg-red-100 text-red-700' :
                      a.severity === 'severe' ? 'bg-red-50 text-red-600' :
                      a.severity === 'moderate' ? 'bg-amber-50 text-amber-600' :
                      'bg-emerald-50 text-emerald-600'
                    }`}>{a.severity?.replace('_', ' ')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ─── Care Transitions ─── */}
      {(isDoctor && isAlreadyDoctor && !isRestricted || (profile.careTransitions || []).length > 0) && (
        <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-7">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-headline text-lg font-bold text-slate-900">Care Transitions</h3>
            {isDoctor && isAlreadyDoctor && !isRestricted && (
              <button
                onClick={() => setShowAddTransition(v => !v)}
                className="flex items-center gap-1 text-xs font-bold text-primary hover:bg-primary/10 px-2.5 py-1.5 rounded-lg transition-all"
              >
                {showAddTransition ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                {showAddTransition ? 'Cancel' : 'Add'}
              </button>
            )}
          </div>
          {showAddTransition && (
            <div className="mb-4 p-4 bg-slate-50 rounded-xl space-y-3 border border-slate-200">
              {/* Care plan templates */}
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Care plan template</p>
                <div className="flex flex-wrap gap-1.5">
                  {suggestedTemplates.map(tpl => (
                    <button key={tpl.id} type="button" onClick={() => applyTemplate(tpl)}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${
                        tpl.matched
                          ? 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20'
                          : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-100'
                      }`}>
                      {tpl.matched && <span className="mr-1">★</span>}{tpl.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={transForm.type} onChange={e => setTransForm(p => ({ ...p, type: e.target.value }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg">
                  <option value="discharge">Hospital Discharge</option>
                  <option value="admission">Hospital Admission</option>
                  <option value="er_visit">ER Visit</option>
                  <option value="clinic_visit">Clinic Visit</option>
                  <option value="home_health_start">Home Health Started</option>
                  <option value="home_health_end">Home Health Ended</option>
                </select>
                <input type="date" value={transForm.date} onChange={e => setTransForm(p => ({ ...p, date: e.target.value }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" />
              </div>
              <input placeholder="Facility (e.g. St. Mary's Hospital)" value={transForm.facility} onChange={e => setTransForm(p => ({ ...p, facility: e.target.value }))} className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" />
              <input placeholder="Reason / Diagnosis (optional)" value={transForm.reason} onChange={e => setTransForm(p => ({ ...p, reason: e.target.value }))} className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" />
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={transForm.followUpRequired} onChange={e => {
                  setTransForm(p => ({ ...p, followUpRequired: e.target.checked }));
                  if (e.target.checked && !doctorAppointments.length) fetchDoctorAppointments();
                }} className="rounded" />
                Follow-up required
              </label>
              {transForm.followUpRequired && (() => {
                const conflict = getConflict(transForm.followUpDate, transForm.followUpTime);
                return (
                  <div className="space-y-2 pl-6 border-l-2 border-primary/20">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-slate-500 font-medium">Date</label>
                        <input type="date" value={transForm.followUpDate} onChange={e => setTransForm(p => ({ ...p, followUpDate: e.target.value }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-slate-500 font-medium">Time</label>
                        <select value={transForm.followUpTime} onChange={e => setTransForm(p => ({ ...p, followUpTime: e.target.value }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg">
                          {Array.from({ length: 23 }, (_, i) => {
                            const h = Math.floor(i / 2) + 7;
                            const m = i % 2 === 0 ? '00' : '30';
                            if (h > 18 || (h === 18 && m === '30')) return null;
                            const val = `${String(h).padStart(2, '0')}:${m}`;
                            const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
                            const label = `${hour12}:${m} ${h < 12 ? 'AM' : 'PM'}`;
                            return <option key={val} value={val}>{label}</option>;
                          })}
                        </select>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-slate-500 font-medium">Type</label>
                      <select value={transForm.followUpType} onChange={e => setTransForm(p => ({ ...p, followUpType: e.target.value }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg">
                        <option value="virtual_visit">Virtual Visit</option>
                        <option value="home_visit">Home Visit</option>
                        <option value="pharmacist_consult">Pharmacist Consult</option>
                      </select>
                    </div>
                    {loadingAppts && <p className="text-xs text-slate-400">Checking your calendar…</p>}
                    {conflict && (
                      <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-700">
                          You already have a <strong>{conflict.appointmentData?.appointmentType?.replace(/_/g, ' ')}</strong> at{' '}
                          {new Date(conflict.appointmentData?.scheduledDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} on this day. Consider choosing a different time.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
              <button onClick={handleAddTransition} disabled={savingTrans} className="w-full py-2 bg-primary text-white rounded-lg text-sm font-bold disabled:opacity-50">
                {savingTrans ? 'Saving...' : 'Save Transition'}
              </button>
            </div>
          )}
          <div className="space-y-3">
            {(profile.careTransitions || []).length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No care transitions recorded yet</p>
            ) : profile.careTransitions.slice(0, 6).map((t) => {
              const typeLabels = { discharge: 'Hospital Discharge', admission: 'Hospital Admission', er_visit: 'ER Visit', clinic_visit: 'Clinic Visit', home_health_start: 'Home Health Started', home_health_end: 'Home Health Ended' };
              const daysSince = Math.floor((Date.now() - new Date(t.date)) / 86400000);
              return (
                <div key={t._id}>
                  {editingItem?.id === t._id ? (
                    <div className="p-3 bg-slate-50 rounded-xl border border-primary/20 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <select value={editingItem.form.type} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, type: e.target.value } }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg">
                          <option value="discharge">Hospital Discharge</option>
                          <option value="admission">Hospital Admission</option>
                          <option value="er_visit">ER Visit</option>
                          <option value="clinic_visit">Clinic Visit</option>
                          <option value="home_health_start">Home Health Started</option>
                          <option value="home_health_end">Home Health Ended</option>
                        </select>
                        <input type="date" value={editingItem.form.date?.slice(0,10)} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, date: e.target.value } }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" />
                      </div>
                      <input value={editingItem.form.facility} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, facility: e.target.value } }))} className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" placeholder="Facility" />
                      <input value={editingItem.form.reason} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, reason: e.target.value } }))} className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" placeholder="Reason / Diagnosis" />
                      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={editingItem.form.followUpRequired} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, followUpRequired: e.target.checked } }))} className="rounded" />
                        Follow-up required
                      </label>
                      {editingItem.form.followUpRequired && (() => {
                        const conflict = getConflict(editingItem.form.followUpDate?.slice(0,10), editingItem.form.followUpTime);
                        return (
                          <div className="space-y-2 pl-6 border-l-2 border-primary/20">
                            <div className="grid grid-cols-2 gap-2">
                              <div className="flex flex-col gap-1">
                                <label className="text-xs text-slate-500 font-medium">Date</label>
                                <input type="date" value={editingItem.form.followUpDate?.slice(0,10) || ''} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, followUpDate: e.target.value } }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" />
                              </div>
                              <div className="flex flex-col gap-1">
                                <label className="text-xs text-slate-500 font-medium">Time</label>
                                <select value={editingItem.form.followUpTime || '09:00'} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, followUpTime: e.target.value } }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg">
                                  {Array.from({ length: 23 }, (_, i) => {
                                    const h = Math.floor(i / 2) + 7;
                                    const m = i % 2 === 0 ? '00' : '30';
                                    if (h > 18 || (h === 18 && m === '30')) return null;
                                    const val = `${String(h).padStart(2, '0')}:${m}`;
                                    const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
                                    return <option key={val} value={val}>{hour12}:{m} {h < 12 ? 'AM' : 'PM'}</option>;
                                  })}
                                </select>
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-xs text-slate-500 font-medium">Type</label>
                              <select value={editingItem.form.followUpType || 'virtual_visit'} onChange={e => setEditingItem(p => ({ ...p, form: { ...p.form, followUpType: e.target.value } }))} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg">
                                <option value="virtual_visit">Virtual Visit</option>
                                <option value="home_visit">Home Visit</option>
                                <option value="pharmacist_consult">Pharmacist Consult</option>
                              </select>
                            </div>
                            {conflict && (
                              <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                                <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                                <p className="text-xs text-amber-700">
                                  You already have a <strong>{conflict.appointmentData?.appointmentType?.replace(/_/g, ' ')}</strong> at{' '}
                                  {new Date(conflict.appointmentData?.scheduledDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} on this day. Consider choosing a different time.
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <div className="flex gap-2">
                        <button onClick={handleSaveEdit} disabled={savingEdit} className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-primary text-white rounded-lg text-xs font-bold disabled:opacity-50"><Check className="w-3.5 h-3.5" />{savingEdit ? 'Saving…' : 'Save'}</button>
                        <button onClick={() => setEditingItem(null)} className="flex-1 py-1.5 bg-slate-200 text-slate-600 rounded-lg text-xs font-bold">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="group flex items-start justify-between p-3 bg-slate-50 rounded-xl border border-outline-variant/10">
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                          t.type === 'discharge' ? 'bg-amber-500' :
                          t.type === 'admission' ? 'bg-red-500' :
                          t.type === 'er_visit' ? 'bg-red-600' : 'bg-blue-500'
                        }`} />
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{typeLabels[t.type] || t.type}</p>
                          {t.facility && <p className="text-xs text-slate-500">{t.facility}</p>}
                          {t.reason && <p className="text-xs text-slate-500 italic">{t.reason}</p>}
                        </div>
                      </div>
                      <div className="flex items-start gap-2 shrink-0 ml-3">
                        <div className="text-right">
                          <p className="text-xs font-bold text-slate-700">{new Date(t.date).toLocaleDateString()}</p>
                          <p className="text-xs text-slate-500">{daysSince}d ago</p>
                          {t.followUpRequired && !t.followUpCompleted && (
                            <p className="text-xs font-bold text-amber-600 mt-0.5 flex items-center gap-1">
                              <CalendarClock className="w-3 h-3" />
                              {t.followUpDate ? `Due ${new Date(t.followUpDate).toLocaleDateString()}` : 'Follow-up needed'}
                            </p>
                          )}
                          {t.followUpCompleted && (
                            <p className="text-xs font-bold text-emerald-600 mt-0.5 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />Follow-up done
                            </p>
                          )}
                        </div>
                        {isDoctor && isAlreadyDoctor && !isRestricted && (<>
                          <button onClick={() => {
                            setEditingItem({ type: 'transition', id: t._id, form: { type: t.type, date: t.date, facility: t.facility || '', reason: t.reason || '', followUpRequired: t.followUpRequired || false, followUpDate: t.followUpDate || '', followUpTime: t.followUpDate ? new Date(t.followUpDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '09:00', followUpType: t.followUpType || 'virtual_visit' } });
                            if (!doctorAppointments.length) fetchDoctorAppointments();
                          }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-primary/10 rounded-lg transition-all"><Pencil className="w-3.5 h-3.5 text-primary" /></button>
                          <button onClick={() => handleDeleteItem('transition', t._id)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded-lg transition-all"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                        </>)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Vitals + Lab Results ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <VitalsCard
          vitals={profile.vitals || []}
          canView={canViewVitals}
          patientId={id}
          isDoctor={isDoctor}
          onAdded={loadProfile}
        />
        <LabResultsCard
          labResults={profile.labResults || []}
          canView={canViewLabResults}
          patientId={id}
          isDoctor={isDoctor}
          onAdded={loadProfile}
        />
      </div>

      {/* ─── Social History + Mental Health ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Social History */}
        <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-7">
          <div className="flex items-center gap-2 mb-5">
            <Users className="w-5 h-5 text-primary" />
            <h3 className="font-headline text-lg font-bold text-slate-900">Social History</h3>
          </div>
          {!canViewSocialHistory ? (
            <div className="flex items-center gap-2 py-6 justify-center">
              <Shield className="w-4 h-4 text-slate-400" />
              <p className="text-sm text-slate-400">Social history access not granted</p>
            </div>
          ) : (() => {
            const sh = profile.socialHistory || {};
            const labelMap = {
              smokingStatus: 'Smoking', alcoholUse: 'Alcohol Use',
              exerciseFrequency: 'Exercise', livingSituation: 'Living Situation', employmentStatus: 'Employment',
            };
            const hasData = ['smokingStatus', 'alcoholUse', 'exerciseFrequency', 'livingSituation', 'employmentStatus'].some(k => sh[k]);
            return hasData ? (
              <div className="space-y-3">
                {Object.entries(labelMap).filter(([k]) => sh[k]).map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-outline-variant/10">
                    <p className="text-sm font-semibold text-slate-900">{label}</p>
                    <p className="text-sm text-slate-600 capitalize">{sh[k]?.replace(/_/g, ' ')}</p>
                  </div>
                ))}
                {sh.notes && <p className="text-xs text-slate-500 italic px-1 mt-1">{sh.notes}</p>}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-6">No social history reported</p>
            );
          })()}
        </div>

        {/* Mental Health */}
        <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-7">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-primary" />
              <h3 className="font-headline text-lg font-bold text-slate-900">Mental Health</h3>
            </div>
            {canViewMentalHealth && (
              <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                {(profile.mentalHealth?.diagnoses || []).length} diagnoses
              </span>
            )}
          </div>
          {!canViewMentalHealth ? (
            <div className="flex items-center gap-2 py-6 justify-center">
              <Shield className="w-4 h-4 text-slate-400" />
              <p className="text-sm text-slate-400">Mental health access not granted</p>
            </div>
          ) : (() => {
            const mh = profile.mentalHealth || {};
            const diagnoses = mh.diagnoses || [];
            const hasMH = diagnoses.length > 0 || mh.phq9Score != null || mh.gad7Score != null;
            return hasMH ? (
              <div className="space-y-3">
                {diagnoses.map((d, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-outline-variant/10">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{d.name}</p>
                      {d.diagnosedAt && <p className="text-xs text-slate-500">Since {new Date(d.diagnosedAt).toLocaleDateString()}</p>}
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold capitalize ${d.status === 'active' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>
                      {d.status}
                    </span>
                  </div>
                ))}
                {(mh.phq9Score != null || mh.gad7Score != null) && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {mh.phq9Score != null && (
                      <div className="p-3 bg-slate-50 rounded-xl text-center border border-outline-variant/10">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">PHQ-9</p>
                        <p className={`text-2xl font-extrabold mt-1 ${mh.phq9Score >= 15 ? 'text-red-600' : mh.phq9Score >= 10 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {mh.phq9Score}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {mh.phq9Score >= 20 ? 'Severe' : mh.phq9Score >= 15 ? 'Mod-severe' : mh.phq9Score >= 10 ? 'Moderate' : mh.phq9Score >= 5 ? 'Mild' : 'Minimal'}
                        </p>
                      </div>
                    )}
                    {mh.gad7Score != null && (
                      <div className="p-3 bg-slate-50 rounded-xl text-center border border-outline-variant/10">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">GAD-7</p>
                        <p className={`text-2xl font-extrabold mt-1 ${mh.gad7Score >= 15 ? 'text-red-600' : mh.gad7Score >= 10 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {mh.gad7Score}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {mh.gad7Score >= 15 ? 'Severe' : mh.gad7Score >= 10 ? 'Moderate' : mh.gad7Score >= 5 ? 'Mild' : 'Minimal'}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {mh.lastScreeningDate && (
                  <p className="text-xs text-slate-500 px-1">Last screening: {new Date(mh.lastScreeningDate).toLocaleDateString()}</p>
                )}
                {mh.notes && <p className="text-xs text-slate-500 italic px-1">{mh.notes}</p>}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-6">No mental health information on file</p>
            );
          })()}
        </div>
      </div>

      {/* ─── Caregivers Panel (doctor view) ─── */}
      {isDoctor && (
        <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-7">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-primary" />
            <h3 className="font-headline text-lg font-bold text-slate-900">Family Caregivers</h3>
          </div>
          {caregivers.length === 0 ? (
            <p className="text-sm text-slate-400 py-2">No active caregivers linked to this patient.</p>
          ) : (
            <div className="space-y-3">
              {caregivers.map(cg => {
                const hoursAgo = cg.lastActiveAt
                  ? Math.floor((Date.now() - new Date(cg.lastActiveAt)) / 3600000)
                  : null;
                const lastSeen = hoursAgo === null ? 'Never logged in'
                  : hoursAgo < 1 ? 'Active recently'
                  : hoursAgo < 24 ? `${hoursAgo}h ago`
                  : `${Math.floor(hoursAgo / 24)}d ago`;
                return (
                  <div key={cg.id} className="flex items-center justify-between gap-4 p-3 bg-slate-50 rounded-xl border border-outline-variant/10">
                    <div>
                      <p className="text-sm font-bold text-slate-800">{cg.name}</p>
                      <p className="text-xs text-slate-500 capitalize">{cg.relationship} · {cg.email}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs font-medium text-slate-500">{lastSeen}</p>
                      <p className="text-[10px] text-slate-400">
                        Joined {new Date(cg.activatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── Private Doctor Notes ─── */}
      {isDoctor && (() => {
        const TAG_COLORS = {
          clinical:       'bg-blue-50 text-blue-600',
          follow_up:      'bg-violet-50 text-violet-600',
          medication:     'bg-emerald-50 text-emerald-600',
          social:         'bg-amber-50 text-amber-600',
          administrative: 'bg-slate-100 text-slate-500',
          other:          'bg-slate-100 text-slate-500',
        };

        const saveNote = async () => {
          if (!newNote.trim()) return;
          setSavingNote(true);
          try {
            const res = await api.post(`/doctor-notes/${id}`, { note: newNote.trim(), tag: newNoteTag });
            setDoctorNotes(prev => [res.data.data, ...prev]);
            setNewNote('');
            setNewNoteTag('clinical');
            setNotePage(1);
            toast.success('Note saved');
          } catch {
            toast.error('Failed to save note');
          } finally {
            setSavingNote(false);
          }
        };

        const updateNote = async () => {
          if (!editingNote?.note.trim()) return;
          setSavingNote(true);
          try {
            const res = await api.patch(`/doctor-notes/${editingNote.id}`, { note: editingNote.note, tag: editingNote.tag });
            setDoctorNotes(prev => prev.map(n => n._id === editingNote.id ? res.data.data : n));
            setEditingNote(null);
            toast.success('Note updated');
          } catch {
            toast.error('Failed to update note');
          } finally {
            setSavingNote(false);
          }
        };

        const deleteNote = async (noteId) => {
          setDeletingNote(noteId);
          try {
            await api.delete(`/doctor-notes/${noteId}`);
            setDoctorNotes(prev => prev.filter(n => n._id !== noteId));
            toast.success('Note deleted');
          } catch {
            toast.error('Failed to delete note');
          } finally {
            setDeletingNote(null);
          }
        };

        return (
          <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-7">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-primary" />
              <h3 className="font-headline text-lg font-bold text-slate-900">Private Notes</h3>
              <span className="flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 ml-1">
                <Lock className="w-2.5 h-2.5" /> Only visible to you
              </span>
            </div>

            {/* New note form */}
            <div className="mb-5 space-y-2">
              <textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="Add a private clinical note…"
                rows={3}
                maxLength={5000}
                className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              />
              <div className="flex items-center justify-between gap-3">
                <select
                  value={newNoteTag}
                  onChange={e => setNewNoteTag(e.target.value)}
                  className="text-xs bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {['clinical','follow_up','medication','social','administrative','other'].map(t => (
                    <option key={t} value={t}>{t.replace('_', ' ')}</option>
                  ))}
                </select>
                <button
                  onClick={saveNote}
                  disabled={savingNote || !newNote.trim()}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-primary text-white text-xs font-bold rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {savingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                  Save note
                </button>
              </div>
            </div>

            {/* Filter pills + counts */}
            {doctorNotes.length > 0 && (() => {
              const usedTags = [...new Set(doctorNotes.map(n => n.tag).filter(Boolean))];
              const countByTag = doctorNotes.reduce((acc, n) => { acc[n.tag] = (acc[n.tag] || 0) + 1; return acc; }, {});
              return (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {['all', ...usedTags].map(tag => {
                    const count = tag === 'all' ? doctorNotes.length : countByTag[tag];
                    const active = noteFilter === tag;
                    return (
                      <button
                        key={tag}
                        onClick={() => { setNoteFilter(tag); setNotePage(1); }}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold capitalize transition-colors ${
                          active
                            ? tag === 'all'
                              ? 'bg-primary text-white'
                              : `${TAG_COLORS[tag]} ring-1 ring-inset ring-current`
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                      >
                        {tag === 'all' ? 'All' : tag.replace('_', ' ')}
                        <span className={`text-[10px] font-black px-1 py-0.5 rounded-full min-w-[16px] text-center leading-none ${
                          active ? (tag === 'all' ? 'bg-white/25 text-white' : 'bg-black/10') : 'bg-slate-200 text-slate-500'
                        }`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}

            {/* Notes list — paginated */}
            {doctorNotes.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-3">No notes yet. Add your first note above.</p>
            ) : (() => {
              const filtered = noteFilter === 'all' ? doctorNotes : doctorNotes.filter(n => n.tag === noteFilter);
              const totalPages = Math.ceil(filtered.length / NOTES_PER_PAGE);
              const page = Math.min(notePage, totalPages || 1);
              const visible = filtered.slice((page - 1) * NOTES_PER_PAGE, page * NOTES_PER_PAGE);
              return filtered.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-3">No {noteFilter.replace('_', ' ')} notes yet.</p>
              ) : (
                <>
                  <div className="space-y-3">
                    {visible.map(note => (
                      <div key={note._id} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                        {editingNote?.id === note._id ? (
                          <div className="space-y-2">
                            <textarea
                              value={editingNote.note}
                              onChange={e => setEditingNote(prev => ({ ...prev, note: e.target.value }))}
                              rows={3}
                              maxLength={5000}
                              className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                            />
                            <div className="flex items-center gap-2">
                              <select
                                value={editingNote.tag}
                                onChange={e => setEditingNote(prev => ({ ...prev, tag: e.target.value }))}
                                className="text-xs bg-white border border-slate-200 rounded-xl px-2 py-1 focus:outline-none"
                              >
                                {['clinical','follow_up','medication','social','administrative','other'].map(t => (
                                  <option key={t} value={t}>{t.replace('_', ' ')}</option>
                                ))}
                              </select>
                              <button onClick={updateNote} disabled={savingNote} className="px-3 py-1 bg-primary text-white text-xs font-bold rounded-lg disabled:opacity-50 flex items-center gap-1">
                                {savingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
                              </button>
                              <button onClick={() => setEditingNote(null)} className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-bold rounded-lg hover:bg-slate-200">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{note.note}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded capitalize ${TAG_COLORS[note.tag] || TAG_COLORS.other}`}>
                                  {note.tag?.replace('_', ' ')}
                                </span>
                                <span className="text-[10px] text-slate-400">
                                  {new Date(note.editedAt || note.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  {note.editedAt && ' (edited)'}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => setEditingNote({ id: note._id, note: note.note, tag: note.tag })}
                                className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => deleteNote(note._id)}
                                disabled={deletingNote === note._id}
                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              >
                                {deletingNote === note._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                      <span className="text-xs text-slate-400">
                        {(page - 1) * NOTES_PER_PAGE + 1}–{Math.min(page * NOTES_PER_PAGE, filtered.length)} of {filtered.length}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setNotePage(p => p - 1)}
                          disabled={page === 1}
                          className="px-2.5 py-1 text-xs font-bold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          ← Prev
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                          <button
                            key={p}
                            onClick={() => setNotePage(p)}
                            className={`w-7 h-7 text-xs font-bold rounded-lg transition-colors ${
                              p === page ? 'bg-primary text-white' : 'text-slate-500 hover:bg-slate-100'
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                        <button
                          onClick={() => setNotePage(p => p + 1)}
                          disabled={page === totalPages}
                          className="px-2.5 py-1 text-xs font-bold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        );
      })()}

      {/* ─── Demographics Card ─── */}
      <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-7">
        <h3 className="font-headline text-lg font-bold text-slate-900 mb-5">Patient Information</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Age', value: profile.demographics?.dateOfBirth ? `${Math.floor((Date.now() - new Date(profile.demographics.dateOfBirth).getTime()) / (365.25 * 86400000))}Y` : '—' },
            { label: 'Lives Alone', value: profile.demographics?.livesAlone ? 'Yes' : 'No' },
            { label: 'Caregiver', value: profile.demographics?.hasCaregiver ? 'Yes' : 'No' },
            { label: 'Mobility', value: profile.functionalStatus?.mobility || '—' },
            { label: 'Contact', value: profile.preferredContactMethod || 'phone' },
            { label: 'Language', value: profile.demographics?.preferredLanguage || 'en' },
            { label: 'Status', value: profile.status || 'active' },
            { label: 'Last Active', value: profile.engagement?.lastActiveAt ? timeSince(profile.engagement.lastActiveAt) : '—' },
          ].map((item, i) => (
            <div key={i} className="bg-slate-50 rounded-xl p-4 border border-outline-variant/10">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{item.label}</p>
              <p className="text-sm font-semibold text-slate-900 capitalize">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
      {symptomPickerModal}
    </div>
  );
};

export default PatientDetailPage;
